import type { LeadSource } from '@/types/lead';

/** Sources a human may *pick* on a lead form. */
export const LEAD_SOURCES: { value: LeadSource; label: string }[] = [
  { value: 'WEBSITE', label: 'Website' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'PHONE_CALL', label: 'Phone Call' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'ANGI', label: 'Angi' },
  { value: 'THUMBTACK', label: 'Thumbtack' },
  { value: 'GOOGLE_LSA', label: 'Google LSA' },
  { value: 'GOOGLE_ADS', label: 'Google Ads' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'YELP', label: 'Yelp' },
  { value: 'NEXTDOOR', label: 'Nextdoor' },
  { value: 'HOMEADVISOR', label: 'HomeAdvisor' },
  { value: 'WALK_IN', label: 'Walk-In' },
  { value: 'CHAT', label: 'Chat' },
  { value: 'INBOX', label: 'Inbox' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Sources the system assigns and a human never picks — the machine origins, the
 * Suite F (F-09) bucket an inbound with no declared channel lands in, and the
 * values auto-creation stamps. Kept out of LEAD_SOURCES so they can't be chosen
 * on a create form, but every *filter* surface has to offer them: they sit on
 * real rows, and a value missing here is a value nobody can segment.
 *
 * MANUAL (NOT-584) is here rather than in LEAD_SOURCES because it answers "how
 * did this row get made", not "where did the customer come from" — there is no
 * marketing dollar behind it, so offering it as a channel poisons attribution.
 * It is the backend default on `lead.source` and is stamped by the create
 * paths, so it stays filterable and stays labelled; it just isn't pickable.
 *
 * ZAPIER and the two DIRECT_* values are listed to make existing rows findable,
 * not to bless them. ZAPIER is a transport, never an acquisition channel
 * (NOT-592 §1.2) — no new lead resolves to it — and the DIRECT_* rows come from
 * the auto-create path NOT-592 §1.8 retires. Both leave the taxonomy in U27.
 */
export const SYSTEM_LEAD_SOURCES: { value: LeadSource; label: string }[] = [
  { value: 'MANUAL', label: 'Manual Entry' },
  { value: 'ONLINE_BOOKING', label: 'Online Booking' },
  { value: 'API', label: 'API' },
  { value: 'ZAPIER', label: 'Zapier' },
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'DIRECT_ESTIMATE', label: 'Direct Estimate' },
  { value: 'DIRECT_JOB', label: 'Direct Job' },
];

/**
 * Every source a row can carry, pickable or not. For *reading* a stored value —
 * labels and orphan-value guards — never for building a picker.
 */
export const ALL_LEAD_SOURCES: { value: LeadSource; label: string }[] = [
  ...LEAD_SOURCES,
  ...SYSTEM_LEAD_SOURCES,
];

/**
 * Label for a persisted source. Resolves the system-assigned values too, so a
 * row stamped MANUAL reads "Manual Entry" on surfaces whose picker no longer
 * offers it instead of leaking the raw enum.
 */
export function leadSourceLabel(value: string): string {
  return ALL_LEAD_SOURCES.find((source) => source.value === value)?.label ?? value;
}

export type LeadSourceFilter = LeadSource | 'all';

/**
 * The one source list every leads *filter* control renders — the split list
 * panel, the table view, and (via SOURCE_VALUES in routes/.../leads.tsx) the
 * set of `?source=` values search validation will keep. One list so a control
 * and the URL contract can never drift: a bucket missing here is a bucket the
 * user cannot segment, which is exactly the F-09 invisibility this closes.
 */
export const LEAD_SOURCE_FILTERS: { value: LeadSourceFilter; label: string }[] = [
  { value: 'all', label: 'All sources' },
  ...ALL_LEAD_SOURCES,
];
